"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useCallback } from "react";
import { headerVariants, slideFromBottomVariants, cardVariants } from "@/lib/animations";

interface EquipmentCategory {
    key: string;
    title: string;
    items: string[];
}

interface ProductYachtEquipmentProps {
    equipment: EquipmentCategory[];
    title: string;
    /** "Expand All" butonu metni (tur programındaki gibi) */
    expandAllLabel?: string;
    /** "Collapse All" butonu metni */
    collapseAllLabel?: string;
}

export default function ProductYachtEquipment({
    equipment,
    title,
    expandAllLabel = "Expand All",
    collapseAllLabel = "Collapse All",
}: ProductYachtEquipmentProps) {
    const [expandedEquipment, setExpandedEquipment] = useState<Record<string, boolean>>({});
    const [allExpanded, setAllExpanded] = useState(false);

    const toggleEquipmentCategory = useCallback((category: string) => {
        if (allExpanded) return;
        setExpandedEquipment(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    }, [allExpanded]);

    const handleExpandAll = useCallback(() => {
        if (allExpanded) {
            setExpandedEquipment({});
            setAllExpanded(false);
        } else {
            const next: Record<string, boolean> = {};
            equipment.forEach((cat) => { next[cat.key] = true; });
            setExpandedEquipment(next);
            setAllExpanded(true);
        }
    }, [allExpanded, equipment]);

    if (equipment.length === 0) return null;

    return (
        <motion.section 
            id="equipments"
            className="mb-[75px] scroll-mt-20"
            variants={slideFromBottomVariants}
        >
            <motion.div className="flex flex-wrap items-center justify-between gap-3 mb-6" variants={headerVariants}>
                <motion.h2 
                    className="text-xl lg:text-3xl font-bold text-alsero-dark-gray"
                >
                    {title}
                </motion.h2>
                <button
                    type="button"
                    onClick={handleExpandAll}
                    className="text-sm text-alsero-dark-gray underline hover:text-alsero-first transition-colors"
                    aria-label={allExpanded ? collapseAllLabel : expandAllLabel}
                    aria-expanded={allExpanded}
                >
                    {allExpanded ? collapseAllLabel : expandAllLabel}
                </button>
            </motion.div>
            
            <motion.div 
                className="space-y-4"
                variants={cardVariants}
            >
                {equipment.map((category) => (
                    <motion.div 
                        key={category.key} 
                        className="bg-white shadow-tour-card rounded-lg overflow-hidden transition-all duration-300 hover:shadow-2xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <motion.button
                            type="button"
                            onClick={() => toggleEquipmentCategory(category.key)}
                            disabled={allExpanded}
                            className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between group disabled:cursor-default disabled:opacity-100"
                            whileHover={allExpanded ? undefined : { x: 2 }}
                            whileTap={allExpanded ? undefined : { scale: 0.99 }}
                            aria-expanded={allExpanded || !!expandedEquipment[category.key]}
                        >
                            <h3 className="font-semibold text-alsero-dark-gray text-left group-hover:text-alsero-first transition-colors">
                                {category.title}
                            </h3>
                            <motion.svg 
                                className="w-5 h-5 text-alsero-first transition-transform"
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                                animate={{ 
                                    rotate: (allExpanded || expandedEquipment[category.key]) ? 180 : 0
                                }}
                                transition={{ 
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20
                                }}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </motion.svg>
                        </motion.button>
                        <AnimatePresence>
                            {(allExpanded || expandedEquipment[category.key]) && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ 
                                        height: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 30
                                        },
                                        opacity: {
                                            duration: 0.2
                                        }
                                    }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-6 py-5 bg-white">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {category.items.map((item, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ 
                                                        delay: idx * 0.05,
                                                        duration: 0.3
                                                    }}
                                                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group/item"
                                                >
                                                    <div className="text-alsero-first flex-shrink-0">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <span className="text-alsero-dark-gray font-medium group-hover/item:text-alsero-first transition-colors">
                                                        {item}
                                                    </span>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))}
            </motion.div>
        </motion.section>
    );
}

